import { GUID } from 'core/utils';
import produce, { Draft, enableES5 } from 'immer';
import * as ActionNames from '../models/actions/names';
import * as commonActionNames from 'src/_common/models/redux/actions/names';
import { PARAMODEL } from '../models/store';
import CONSTANTS, { ONBOARDING_USER } from '../constants';
import * as AuthAction from 'src/authentication/models/action';
import { CHAR_LIMITS } from 'config/constants';
import { getWordCount, getWordIndexes, inputWordLimitCheck } from '@Common/handlers/helper';

const initState: PARAMODEL = {
  toggleState: false,
  updateOutputSentenceKey: false,
  showAcceptTncDialog: false,
  firstParaphrase: true,
  isOutputBoxEditableForMobile: undefined,
  lastSubmission: '',
  showRephrase: false,
  uploadedFileInfo: { fileName: '', fileType: '' },
  numberOfCompletedParaphrases: 0,
  showCharLimitMessage: false,
  startedQuilling: false,
  numberOfSentencesInArticle: 0,
  showRequillLabel: null,
  inputSentences: {
    inputId: GUID(),
    sentenceList: [],
  },
  focusedInputParaphraser: null,
  inputText: '',
  quillConfigOpenInMobile: false,
  outputParaphraseUpdater: {},
  compareModeUpdater: false,
  strength: 2,
  settings: {
    showLegend: false,
    useYellowToHighlightSentence: false,
    showLongestUnchangedWordColor: true,
    showChangeWordColor: true,
    showSentenceRestructureLine: true,
  },
  showContextModel: false,
  articleKey: 'articleKey',
  lockedSentenceCount: 0,
  userOnboardingStatus: false,
  isDialogOpen: false,
  currentOnboardingStep: -1,
  inputWordLimit: CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_GUEST,
  freezeWords: [],
  wordFlipperValue: 0,
  userClickedOnRephraseCount: -1,
  lastSelectedModes: [],
  rephraseTooltipState: false,
  hideSentenceControl: false,
  showMergeModal: false,
  showPersistedSentenceControl: false,
};

enableES5();

const paraphraserReducer = produce((draft: Draft<PARAMODEL> = initState, action: any) => {
  switch (action.type) {
    // case ActionNames.SHOW_TNC_DIALOG:
    //   draft.showAcceptTncDialog = action.payload;
    //   break;

    // case ActionNames.FIRST_PARAPHRASE:
    //   draft.firstParaphrase = action.payload;
    //   break;

    // case ActionNames.TOGGLE_STATE:
    //   draft.toggleState = !draft.toggleState;
    //   break;

    case ActionNames.OUTPUTBOX_EDITABLE_MOBILE:
      draft.isOutputBoxEditableForMobile = action.payload.value;
      break;

    case ActionNames.SET_UPLOAD_FILE_INFO:
      draft.uploadedFileInfo = action.payload.value;
      break;

    case ActionNames.SET_NUMBER_OF_COMPLETED_PARAPHRASES:
      draft.numberOfCompletedParaphrases += 1;
      if (draft.numberOfCompletedParaphrases === draft.inputSentences?.sentenceList?.length) {
        draft.startedQuilling = false;

        /* Handling the case in which user rephrasing the article
            on the same mode for rephrase tooltip 
            also pushing the strength when the lastSelectedModes Array is empty*/
        if (
          draft.lastSelectedModes.length &&
          draft.strength !== draft.lastSelectedModes[draft.lastSelectedModes.length - 1]
        ) {
          if (!draft.lastSelectedModes.includes(draft.strength))
            draft.lastSelectedModes = [...draft.lastSelectedModes, draft.strength];
          else draft.rephraseTooltipState = true;
        } else if (!draft.lastSelectedModes.length) draft.lastSelectedModes = [draft.strength];

        draft.showRephrase = true;
        draft.userClickedOnRephraseCount += 1;
        if (draft.firstParaphrase) draft.firstParaphrase = false;
      }
      break;

    case ActionNames.SET_SHOW_CHAR_LIMIT_MESSAGE:
      draft.showCharLimitMessage = action.payload.value;
      break;

    case ActionNames.SET_STARTED_QUILLING:
      // if (draft.updateOutputSentenceKey) {
      //   draft.articleKey = GUID();
      //   draft.updateOutputSentenceKey = false;
      // }
      draft.startedQuilling = action.payload.value;
      if (draft.startedQuilling) {
        if (!draft.showRephrase) draft.focusedInputParaphraser = null;
        draft.numberOfCompletedParaphrases = 0;
        if (!draft.inputText?.trim()?.length) draft.inputText = CONSTANTS.DEMO_DEFAULT;

        draft.lastSubmission = draft.inputText;
        const wordIndexes = getWordIndexes(draft.inputText);
        if (draft.inputWordLimit > 0 && getWordCount(wordIndexes) > draft.inputWordLimit) {
          let limitIndex = inputWordLimitCheck(wordIndexes, draft.inputWordLimit);
          draft.lastSubmission = draft.inputText.substr(0, limitIndex);
        } else {
          draft.showCharLimitMessage = false;
        }

        if (draft.lockedSentenceCount >= draft.inputSentences?.sentenceList?.length)
          draft.lockedSentenceCount = 0;

        // Handling this for clickAwayListener of rephrase tooltip.
        draft.rephraseTooltipState = false;
      } else {
        // This will execute whenever the started quilling is set to be false
        //  And when There are some input sentences present
        if (draft.inputSentences?.sentenceList.length) {
          if (draft.firstParaphrase) draft.firstParaphrase = false;
          draft.showRephrase = true;
          draft.userClickedOnRephraseCount += 1;
        }
      }
      break;

    // case ActionNames.SET_NUMBER_OF_SENTENCES_IN_ARTICLE:
    //   draft.numberOfSentencesInArticle = action.payload.value;
    //   break;

    case ActionNames.SET_SHOW_REQUILL_LABEL:
      draft.showRequillLabel = action.payload.value;
      break;

    case ActionNames.SET_INPUT_SENTENCES:
      draft.inputSentences = action.payload.value;
      draft.numberOfSentencesInArticle = draft.inputSentences.sentenceList?.length;
      draft.outputParaphraseUpdater = {};
      if (draft.inputSentences?.sentenceList.length) {
        draft.inputSentences?.sentenceList.forEach((para) => {
          if (para.pID) draft.outputParaphraseUpdater[para.pID] = 0;
        });
      }

      if (draft.freezeWords.length)
        draft.inputSentences.sentenceList = handleFreezeWordCount(
          draft.inputSentences.sentenceList,
          draft.freezeWords
        );

      if (draft.lockedSentenceCount > 0) draft.lockedSentenceCount = 0;

      if (draft.updateOutputSentenceKey) {
        draft.articleKey = GUID();
        draft.updateOutputSentenceKey = false;
      }
      break;

    case ActionNames.SET_FOCUSED_INPUT_PARAPHRASER:
      draft.focusedInputParaphraser = action.payload.value;
      break;

    case ActionNames.SET_INPUT_TEXT:
      draft.inputText = action.payload.value;
      const wordIndexes = getWordIndexes(draft.inputText);
      const inputTextWordCount = getWordCount(wordIndexes);
      let limitIndex = draft.inputText.length;
      if (draft.inputWordLimit > 0 && inputTextWordCount > draft.inputWordLimit) {
        limitIndex = inputWordLimitCheck(wordIndexes, draft.inputWordLimit);
      }
      if (!draft.inputText.trim().length) draft.uploadedFileInfo = { fileName: '', fileType: '' };

      if (
        draft.inputText.substr(0, limitIndex) !== draft.lastSubmission &&
        draft.inputSentences.sentenceList?.length
      )
        draft.showRephrase = false;

      if (
        draft.inputText.substr(0, limitIndex) === draft.lastSubmission &&
        draft.inputSentences.sentenceList?.length
      )
        draft.showRephrase = true;

      if (draft.inputWordLimit > 0 && inputTextWordCount > draft.inputWordLimit)
        draft.showCharLimitMessage = true;

      if (
        draft.inputWordLimit <= 0 ||
        (inputTextWordCount <= draft.inputWordLimit && draft.showCharLimitMessage)
      )
        draft.showCharLimitMessage = false;

      break;

    case ActionNames.SET_CYRILLIC_FREE_TEXT:
      draft.inputText = action.payload.value;
      draft.lastSubmission = draft.inputText;
      break;

    case ActionNames.USER_CLICKED_TRY_SAMPLE:
      if (draft.currentOnboardingStep === ONBOARDING_USER.sampleInputText.length - 2) {
        draft.inputText =
          ONBOARDING_USER.sampleInputText[ONBOARDING_USER.sampleInputText.length - 1];
        draft.currentOnboardingStep = ONBOARDING_USER.sampleInputText.length - 1;
        draft.userOnboardingStatus = false;
        return;
      }
      draft.inputText = ONBOARDING_USER.sampleInputText[draft.currentOnboardingStep + 1];
      draft.currentOnboardingStep = draft.currentOnboardingStep + 1;
      draft.showRephrase = false;
      break;

    case ActionNames.SET_QUILL_CONFIG_OPEN_MOBILE:
      draft.quillConfigOpenInMobile = action.payload.value;
      break;

    case ActionNames.SET_OUTPUT_PARAPHRASER_UPDATER:
      // if (draft.updateOutputSentenceKey) {
      //   draft.articleKey = GUID();
      //   draft.updateOutputSentenceKey = false;
      // }
      // if (!Object.keys(action.payload.value).length) {
      //   draft.outputParaphraseUpdater = {};
      //   return;
      // }
      // if (action.payload.value.key === 0 && action.payload.value.pID) {
      //   draft.outputParaphraseUpdater[action.payload.value.pID] = 0;
      //   return;
      // }
      if (action.payload.length) {
        action.payload.forEach((pID: string) => {
          if (pID) draft.outputParaphraseUpdater[pID] += 1;
        });
      }
      break;

    case ActionNames.SET_COMPARE_MODE_UPDATER:
      draft.compareModeUpdater = !draft.compareModeUpdater;
      break;

    case ActionNames.SET_STRENGTH:
      draft.strength = action.payload.value;
      break;

    case ActionNames.SET_SHOW_LEGEND:
      draft.settings.showLegend = action.payload.value;
      break;

    case ActionNames.SET_YELLOW_TO_HIGHLIGHT_SENTENCE:
      draft.settings.useYellowToHighlightSentence = action.payload.value;
      break;

    case ActionNames.SET_SHOW_LONGEST_UNCHANGED_WORD_COLOR:
      draft.settings.showLongestUnchangedWordColor = action.payload.value;
      break;

    case ActionNames.SET_SHOW_CHANGE_WORD_COLOR:
      draft.settings.showChangeWordColor = action.payload.value;
      break;

    case ActionNames.SET_SHOW_SENTENCE_RESTRUCTURE_LINE:
      draft.settings.showSentenceRestructureLine = action.payload.value;
      break;

    case ActionNames.SET_SENTENCE_SPLITTER_CONTEXT_MESSAGE:
      draft.showContextModel = action.payload.value;
      if (draft.lockedSentenceCount) draft.lockedSentenceCount = 0;
      break;

    case ActionNames.USER_RESETTED_PARAPHRASE_STATE:
      if (draft.updateOutputSentenceKey) {
        draft.articleKey = GUID();
        draft.updateOutputSentenceKey = false;
      }
      draft.inputText = '';
      draft.showRephrase = false;
      draft.showCharLimitMessage = false;
      draft.outputParaphraseUpdater = {};
      draft.focusedInputParaphraser = null;
      draft.inputSentences = {
        inputId: GUID(),
        sentenceList: [],
      };
      draft.uploadedFileInfo = { fileName: '', fileType: '' };
      break;

    case commonActionNames.UI_TOGGLE_DRAWER:
      if (action.payload && draft.showRequillLabel) {
        draft.showRequillLabel = false;
      }
      break;

    case commonActionNames.COMPARE_TOGGLE_DRAWER:
      if (action.payload && draft.showRequillLabel) {
        draft.showRequillLabel = false;
      }
      break;

    case ActionNames.USER_FINISHED_TYPING:
      draft.updateOutputSentenceKey = true;
      break;

    case ActionNames.USER_CHANGED_SENTENCE_LOCK_STATUS:
      draft.lockedSentenceCount = action.payload.value;
      break;

    case ActionNames.USER_ONBOARDING_STATUS:
      draft.userOnboardingStatus = action.payload.value;
      break;

    case ActionNames.USER_ONBOARDING_STEP:
      draft.currentOnboardingStep = action.payload.value;
      break;

    case ActionNames.GET_LOCAL_STORAGE_DATA:
      draft.strength = action.payload.strength;
      draft.settings.showLegend = action.payload.showLegend;
      draft.settings.useYellowToHighlightSentence = action.payload.useYellowToHighlightSentence;
      draft.settings.showLongestUnchangedWordColor = action.payload.showLongestUnchangedWordColor;
      draft.settings.showChangeWordColor = action.payload.showChangeWordColor;
      draft.settings.showSentenceRestructureLine = action.payload.showSentenceRestructureLine;
      draft.wordFlipperValue = action.payload.wordFlipperValue;
      break;

    case commonActionNames.PARAPHRASER_RECEIVED_OTHER_PRODUCT_DATA:
      draft.uploadedFileInfo = { fileName: '', fileType: '' };
      break;

    case AuthAction.LOGOUT_USER:
      draft.inputWordLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_GUEST;
      draft.freezeWords = [];
      if ([0, 2].indexOf(draft.strength) === -1) {
        draft.strength = 2;
      }
      if (draft.wordFlipperValue >= 3) draft.wordFlipperValue = draft.strength === 2 ? 1 : 0;
      draft.startedQuilling = false;
      break;

    case AuthAction.UPDATE_USER_ADDITIONAL_DETAILS:
      if (action.payload.premium) {
        draft.inputWordLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_PREMIUM;
      } else {
        draft.inputWordLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_MEMBER;
      }
      break;

    case ActionNames.UNDO_LAST_PARAPHRASED_STATE:
      draft.inputText = action.payload?.inputText;
      draft.inputSentences = action.payload.inputSentences;
      draft.numberOfSentencesInArticle = draft.inputSentences.sentenceList?.length;
      // draft.focusedInputParaphraser = draft.inputSentences?.sentenceList[0];
      draft.outputParaphraseUpdater = {};
      if (draft.inputSentences?.sentenceList.length) {
        action.payload.inputSentences.sentenceList.map((eachSentence: any) => {
          draft.outputParaphraseUpdater[eachSentence.pID] = 0;
        });
      }
      draft.numberOfCompletedParaphrases = draft.inputSentences.sentenceList?.length;
      draft.showRephrase = true;
      draft.lastSubmission = draft.inputText;
      if (action.payload?.freezeWords?.length) draft.freezeWords = action.payload?.freezeWords;
      break;

    case ActionNames.SET_FREEZE_WORDS:
      draft.freezeWords = [...action.payload];
      draft.inputSentences.sentenceList = handleFreezeWordCount(
        draft.inputSentences.sentenceList,
        draft.freezeWords
      );
      break;

    case ActionNames.USER_CHANGED_WORD_FLIPPER:
      draft.wordFlipperValue = action.wordFlipperValue;
      break;

    case ActionNames.SET_BEAMS:
      if (draft.focusedInputParaphraser) {
        draft.focusedInputParaphraser.strength = action.payload?.strength;
        if (draft.focusedInputParaphraser.beamsPerStrength)
          draft.focusedInputParaphraser.beamsPerStrength[action.payload.strength] =
            action.payload?.beamSet;
        else {
          draft.focusedInputParaphraser.beamsPerStrength = {};
          draft.focusedInputParaphraser.beamsPerStrength[action.payload.strength] =
            action.payload?.beamSet;
        }
      }
      break;
    case ActionNames.IS_DIALOG_OPEN:
      draft.isDialogOpen = action.payload;
      break;

    case ActionNames.SET_REPHRASE_TOOLTIP:
      draft.rephraseTooltipState = action.payload;
      break;

    case ActionNames.SET_SENTENCE_CONTROL_TOOLBAR_STATE:
      draft.hideSentenceControl = action.payload;
      break;

    case ActionNames.SHOW_PERSISTED_SENTENCE_CONTROL:
      draft.showPersistedSentenceControl = action.payload;
      break;

    case ActionNames.SET_MERGE_MODAL_STATE:
      draft.showMergeModal = action.payload;
      break;

    default:
      return draft;
  }
}, initState);

export default paraphraserReducer;

/**
 * Counts number of freeze word in each sentence.
 */
const handleFreezeWordCount = (sentenceList: Array<any>, freezeWords: Array<string>) => {
  return sentenceList.map((sentence) => {
    let count = 0;
    freezeWords.forEach((fWord: string) => {
      let freezeWordRegExp = new RegExp(`(^| )(${fWord})(?=[ ?.,!]|$)`, 'gim');
      if (sentence?.text?.match(freezeWordRegExp)?.length) count += 1;
    });
    return {
      ...sentence,
      freezeWordCount: count,
    };
  });
};
